import { WaitingMember } from '../../shared/waitingMember';
import { useEffect, useRef, useState } from 'react';
import { singletonHook } from 'react-singleton-hook';
import { callActor } from '../../features/call';
import { useAppRouter } from '../useAppRouter';
import { getPublicRuntimeConfig } from '../../libs/init/getPublicRuntimeConfig';
import { CallState, CallTransition } from '../../features/call/call.types';
import { useAppDispatch } from '../useReduxToolkit';
import { setStartCallTime } from '../../features/apolloSession/apolloSession.slice';
import { useGetTime } from '../useGetTime';
import {
  TestCallParticipant,
  getTestCallParticipant,
} from './getTestCallParticipant';
import { getTestWaitingMember } from './getTestWaitingMember';
import { useTestPatientTracking } from './userTestPatientTracking';
import { getLogger } from 'loglevel';
import { setIsPatientQueueOpen } from '../../features/dashboardUi/dashboardUi.slice';
import { Browser, getViewport } from '@doxyme/ui-foundation/hooks';
import { useBrowser } from '../useBrowser';
import { usePersonalRoom } from '../usePersonalRoom';
import { MediaEventType, SpeakerId, media } from '../../webrtc/media';

type UseTestPatientReturnValue = {
  waitingMember?: WaitingMember;
  callParticipant?: TestCallParticipant;
  isTestCallRunning: boolean;
  stopTestCall: (callState: CallState) => void;
  startTestCall: () => void;
};
const addTestPatientUserPilotWindowEventName = 'userpilot:add-test-patient';

const hookInitValues = {
  waitingMembers: [],
  callParticipants: [],
  isTestCallRunning: false,
  startTestCall: () => {},
  stopTestCall: () => {},
};

const logger = getLogger('UseTestPatient');
/**
 * @TODO: Replace singletonHook with Redux state / selectors / regular hooks.
 * We should avoid using singletonHooks as they got registered globally so hard to separate between Provider / Patient.
 */
export const useTestPatientImpl = () => {
  const router = useAppRouter();
  const dispatch = useAppDispatch();
  const getTime = useGetTime();
  const {
    systemInfo: { browser },
  } = useBrowser();
  const { id: personalRoomId } = usePersonalRoom();

  const [waitingMember, setWaitingMember] = useState<WaitingMember>();
  const [callParticipant, setCallParticipant] = useState<TestCallParticipant>();

  const selectedDeviceChangeUnsubscribeRef = useRef<() => void>(
    () => undefined,
  );
  const isTestPatientTrackingEnabled = !!(waitingMember || callParticipant);

  useTestPatientTracking(isTestPatientTrackingEnabled);

  const startTestCall = async () => {
    logger.info('startTestCall');

    /**
     * To start a test call we would ask for media access in Safari (to fix the volume issue)
     */
    try {
      await media.startLocalMedia({});
      logger.info('startLocalMedia success');
    } catch {
      logger.info('startLocalMedia failed');
    }

    void router.push(getPublicRuntimeConfig().paths.testCall);
    callActor.send(CallTransition.START_TEST_CALL);

    const testCallParticipant = getTestCallParticipant(personalRoomId ?? '');
    testCallParticipant.videoElement.addEventListener('pause', () => {
      testCallParticipant.videoElement.play();
    });

    /**
     * Handle speaker change.
     */
    selectedDeviceChangeUnsubscribeRef.current = media.on(
      [MediaEventType.SelectedDevicesChange],
      ({ data }) => {
        if (data?.['field'] === 'speakerId') {
          const currentTime = testCallParticipant.videoElement.currentTime;
          testCallParticipant.videoElement?.setSinkId?.(
            data?.['value'] as SpeakerId,
          );
          /**
           * In Safari when you apply setSinkId the currentTime gets reset and starts playing from the beginning of the current buffer.
           * To fix this we reapply the currentTime after setting the sinkId.
           */
          if (browser === Browser.Safari) {
            testCallParticipant.videoElement.currentTime = currentTime;
          }
        }
      },
    );

    const addTestPatientToTheCall = () => {
      setCallParticipant(testCallParticipant);
      setWaitingMember(undefined);
    };

    /**
     * Add the test call participant to the call when the video is loaded. Before it will be just a loading screen.
     */
    testCallParticipant.videoElement.addEventListener(
      'loadedmetadata',
      addTestPatientToTheCall,
      { once: true },
    );

    /**
     * In Safari when videoElement loose its parent the currentTime got reset and starts  plying from the current buffer part from the beginning
     * causing an repeating affect.
     * It is not really possible to fix the issue within out current layout but at least
     * we can catch it and reset the time to the expect value.
     */
    if (browser === Browser.Safari) {
      let previousCurrentTime = 0;
      /**
       * No need to remove event listener as the startTestCall will be called only once.
       */
      testCallParticipant.videoElement.addEventListener('timeupdate', () => {
        const currentTime = testCallParticipant.videoElement.currentTime;
        // 300ms is enough to avoid triggering when it is not needed (it cannot be less then 250ms but from testing never get more then 300ms)
        if (previousCurrentTime - currentTime > 0.3) {
          testCallParticipant.videoElement.currentTime =
            /**
             * https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/timeupdate_event
             * Use 250ms duration as timeupdate event is fired every 250ms (it could vary by specification but from my testing it was 250ms even in Safari)
             */
            previousCurrentTime + 0.25;
        }
        previousCurrentTime = currentTime;
      });
    }

    dispatch(setStartCallTime(getTime()));
  };

  const stopTestCall = (callState: CallState) => {
    if (callParticipant) {
      callParticipant.videoElement.pause();
      callParticipant.videoElement.src = '';
      callParticipant.videoElement.remove();
      selectedDeviceChangeUnsubscribeRef.current();
    }
    logger.info('stopTestCall', { callState });
    setWaitingMember(undefined);
    setCallParticipant(undefined);
  };

  useEffect(() => {
    const addTestPatient = () => {
      logger.info('addTestPatient');
      if (getViewport().viewportType === 'medium') {
        dispatch(setIsPatientQueueOpen(true));
      }
      setWaitingMember(getTestWaitingMember(personalRoomId ?? ''));
    };

    window.addEventListener(
      addTestPatientUserPilotWindowEventName,
      addTestPatient,
    );

    return () => {
      window.removeEventListener(
        addTestPatientUserPilotWindowEventName,
        addTestPatient,
      );
    };
  }, [callParticipant, personalRoomId, dispatch]);

  return {
    waitingMember,
    callParticipant,
    isTestCallRunning: !!callParticipant,
    startTestCall,
    stopTestCall,
  };
};

export const useTestPatient = singletonHook<UseTestPatientReturnValue>(
  hookInitValues,
  () => useTestPatientImpl(),
);
